import { useEffect, useId } from 'react';

// Shared body-scroll lock. Multiple components (mobile drawer, filter sheets,
// modals) may all want to lock the page at once; without a shared holder
// set, the "save prev / restore prev" pattern poisons itself — one locker's
// captured `prev` becomes another locker's already-released 'hidden', and
// the body stays stuck after every component has technically released.
//
// This module is the single source of truth: it captures the body's original
// overflow exactly once (when the holder set goes 0 → 1) and restores it
// exactly once (when the set goes back to 0). Each locker passes its own
// stable id so duplicate acquire/release pairs are idempotent.

const holders = new Set();
let originalOverflow = '';

function isBrowser() {
  return typeof document !== 'undefined' && document.body;
}

function acquire(id) {
  if (!isBrowser()) return;
  if (holders.size === 0) {
    originalOverflow = document.body.style.overflow || '';
    document.body.style.overflow = 'hidden';
  }
  holders.add(id);
}

function release(id) {
  if (!isBrowser()) return;
  if (!holders.delete(id)) return;
  if (holders.size === 0) {
    document.body.style.overflow = originalOverflow;
    originalOverflow = '';
  }
}

// App-level safety net: clear every holder and force the body back to its
// scrollable state. Wired into route changes so a stuck state from one
// screen can never bleed into the next.
export function releaseAllBodyScrollLocks() {
  if (!isBrowser()) return;
  if (holders.size === 0 && !document.body.style.overflow) return;
  holders.clear();
  document.body.style.overflow = '';
  originalOverflow = '';
}

// React hook: lock the body when `active` is true, release on close or unmount.
// `id` is auto-generated per call site via useId() so each consumer holds a
// unique slot in the shared set.
export function useBodyScrollLock(active) {
  const id = useId();
  useEffect(() => {
    if (!active) return;
    acquire(id);
    return () => release(id);
  }, [active, id]);
}
